import type { Reducer } from "react";
import type { Carousel, State, Action, Settings, Theme } from "types/carousel";

import palettes from "../utils/palettes";

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const initialState: State = {
  activeTab: "Content",
  settings: {
    headshot: "",
    headshotActive: false,
    name: "",
    nameActive: false,
    handle: "",
    handleActive: false,
    removeWatermark: false,
    showSlideNumbers: true,
    fonts: {
      size: "medium",
      titleFamily: "Inter",
      titleWeight: "700",
      textFamily: "Inter",
      textWeight: "400",
    }
  },
  theme: {
    colors: palettes[0],
    background: {
      type: "circles",
      active: true,
    },
  },
  carousels: [
    {
      id: "27d69ea7-2150-4fc3-b5c4-8a0e31857e08",
      type: "introduction",
      title: "GROW YOUR PERSONAL BRAND",
      titleActive: true,
      subtitle: "Cover slide subtitle",
      subtitleActive: true,
      text: "Cover slide subtitle",
      textActive: true,
    },
    {
      id: "6d473e63-6951-4510-b673-07a57fd1b176",
      type: "content",
      title: "Slide 1",
      titleActive: true,
      subtitleActive: false,
      text: "Slide 1 text",
      textActive: true,
    },
    {
      id: "09e2df0-89fd-4ec2-8992-919542bb2e7b",
      type: "content",
      title: "Slide 2",
      titleActive: true,
      subtitleActive: false,
      text: "Slide 2 text",
      textActive: true,
    },
    {
      id: "53fe5d74-0334-42bb-b0ed-3698ef480bf8",
      type: "tldr",
      title: "tldr Slide",
      titleActive: true,
      subtitleActive: false,
      text: "tldr slide  text",
      textActive: true,
    },
    {
      id: "6ad6ddd7-0934-4e63-8f2c-7d655ac38f97",
      type: "outro",
      title: "Outro Slide",
      titleActive: true,
      subtitleActive: false,
      text: "Outro slide text",
      textActive: true,
    },
  ],
};
const createBlankCarousel = (): Carousel => ({
  id: uuid(),
  type: "content",
  title: "",
  titleActive: false,
  subtitle: "",
  subtitleActive: false,
  text: "",
  textActive: false,
});

const appReducer: Reducer<State, Action> = (state, action) => {
  let newState;
  switch (action.type) {
    case "LOAD_FROM_LOCAL_STORAGE":
      newState = action.payload as State;
      break;
    case "RESET":
      newState = initialState;
      break;
    case "UPDATE_ACTIVE_TAB":
      newState = { ...state, activeTab: action.payload as string };
      break;
    case "ADD_CAROUSEL":
      const newCarousel = action.payload as Carousel;
      const newCarousels = [...state.carousels];
      const tldrIndex = state.carousels.findIndex(
        (carousel) => carousel.type === "tldr"
      );
      newCarousels.splice(tldrIndex, 0, newCarousel);
      newState = {
        ...state,
        carousels: newCarousels,
      };
      break;
    case "UPDATE_CAROUSEL":
      const { id, key, value } = action.payload as {
        id: string;
        key: string;
        value: string | boolean;
      };
      const carouselIndex = state.carousels.findIndex(
        (carousel) => carousel.id === id
      );
      const carousel = state.carousels[carouselIndex];
      const updatedCarousel = {
        ...carousel,
        [key]: value,
      };
      const updatedCarousels = [...state.carousels];
      updatedCarousels[carouselIndex] = updatedCarousel;
      newState = {
        ...state,
        carousels: updatedCarousels,
      };
      break;
    case "REORDER_CAROUSELS":
      return {
        ...state,
        carousels: [
          state.carousels[0],
          ...(action.payload as Carousel[]),
          state.carousels[state.carousels.length - 2],
          state.carousels[state.carousels.length - 1],
        ],
      };
    case "RECREATE_CAROUSEL":
      newState = {
        ...state,
        carousels: action.payload as Carousel[],
      };
      break;
    case "REMOVE_CAROUSEL":
      const carouselId = action.payload as string;
      const carouselToRemoveIndex = state.carousels.findIndex(
        (carousel) => carousel.id === carouselId
      );
      const carousels = [...state.carousels];
      carousels.splice(carouselToRemoveIndex, 1);
      newState = {
        ...state,
        carousels,
      };
      break;
    case "UPDATE_SETTINGS":
      const { key: settingsKey, value: settingValue } = action.payload as {
        key: string;
        value: string | boolean;
      };
      newState = {
        ...state,
        settings: {
          ...state.settings,
          [settingsKey]: settingValue,
        },
      };
      break;
    case "UPDATE_FONTS":
      const { key: fontsKey, value: fontsValue } = action.payload as {
        key: string;
        value: string;
      };
      newState = {
        ...state,
        settings: {
          ...state.settings,
          fonts: {
            ...state.settings.fonts,
            [fontsKey]: fontsValue,
          },
        },
      };
      break;
    case "UPDATE_COLORS":
      const { key: colorsKey, value: colorsValue } = action.payload as {
        key: string;
        value: string;
      };
      newState = {
        ...state,
        theme: {
          ...state.theme,
          colors: {
            ...state.theme.colors,
            [colorsKey]: colorsValue,
          },
        },
      };
      break;
    case "UPDATE_BACKGROUND":
      const { key: backgroundKey, value: backgroundValue } = action.payload as {
        key: string;
        value: string | boolean;
      };
      newState = {
        ...state,
        theme: {
          ...state.theme,
          background: {
            ...state.theme.background,
            [backgroundKey]: backgroundValue,
          },
        },
      }
      break;
  }

  // Save state to localStorage
  if (typeof window !== "undefined" &&
    window.location.search !== "?tab=my-carousels"
  ) {
    try {
      newState.id = uuid();
      localStorage.setItem("carousel-state", JSON.stringify(newState));
    } catch (error) {
      console.error("Error saving state to localStorage:", error);
    }
  }
  return newState;
};

export { initialState as appInitialState };
export { createBlankCarousel }
export default appReducer;
